import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Container, Snackbar } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

interface ToastContextValue {
  showToast: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }

  return context
}

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const MAX_VISIBLE = 2
  const [toasts, setToasts] = useState<Array<{ id: string; message: string }>>([])
  const [toastQueue, setToastQueue] = useState<string[]>([])

  const enqueueOrShow = useCallback((message: string) => {
    setToasts((prev) => {
      if (prev.length < MAX_VISIBLE) {
        return [...prev, { id: `${Date.now()}_${Math.random().toString(16).slice(2)}`, message }]
      }

      setToastQueue((q) => [...q, message])
      return prev
    })
  }, [])

  const showToast = useCallback((message: string) => {
    enqueueOrShow(message)
  }, [enqueueOrShow])

  useEffect(() => {
    if (toastQueue.length === 0) {
      return
    }

    if (toasts.length >= MAX_VISIBLE) {
      return
    }

    const [next, ...rest] = toastQueue
    setToastQueue(rest)

    const id = window.setTimeout(() => {
      setToasts((prev) => [...prev, { id: `${Date.now()}_${Math.random().toString(16).slice(2)}`, message: next }])
    }, 60)

    return () => window.clearTimeout(id)
  }, [toastQueue, toasts.length])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          onClose={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          message={
            <Container maxWidth="md" sx={{ px: '16px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%',
                  backgroundColor: 'var(--primary-colour)',
                  color: 'var(--primary-text-colour)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  boxShadow: '0 10px 26px rgba(0, 0, 0, 0.45)',
                  minHeight: 52,
                  py: 1.5,
                  px: 2,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 20, opacity: 0.95 }} />
                <Box
                  sx={{
                    fontFamily: 'inherit',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    lineHeight: 1.25,
                    textAlign: 'center',
                  }}
                >
                  {toast.message}
                </Box>
              </Box>
            </Container>
          }
          autoHideDuration={1500}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{
            bottom: `${72 + index * 66}px !important`,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 3000,
          }}
          ContentProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              padding: 0,
              fontFamily: 'inherit',
            },
          }}
        />
      ))}
    </ToastContext.Provider>
  )
}
